import { useMemo, useState } from "react";
import moment from "moment";
import { ORDER_TYPES } from "../../../../../../consts/api";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { hasLength, unwrapOr } from "../../../../../../utilities/general";
import { uiNotification } from "../../../../../../services/UINotificationService";
import { useSalesChurnReportsQuery } from "../../../../../../api/queries/useSalesChurnReportsQuery";

const prepareReqOrder = (orderState) => {
  if (Object.values(orderState).every(Boolean)) {
    return {
      by: orderState.by,
      direction: orderState.direction,
    };
  }
  return null;
};

const prepareReqPeriod = (periodState) => {
  return {
    from: periodState.from || null,
    to: periodState.to || null,
  };
};

const prepareReqFilter = (filterState) => {
  return {
    clinicIds: filterState.clinics.map((c) => c.value),
    tierIds: filterState.tiers.map((t) => t.value),
    churnReason: filterState.churnReason || null,
  };
};

export function useReports() {
  const { tCommon } = useAppTranslation.Common();

  /* Modifiers */

  const [order, setOrder] = useState({
    by: "",
    direction: "",
  });

  const updateOrder = (orderFieldKey) => {
    setOrder({
      by: orderFieldKey,
      direction:
        order.direction === ORDER_TYPES.desc
          ? ORDER_TYPES.asc
          : ORDER_TYPES.desc,
    });
  };

  const [filter, setFilter] = useState({
    clinics: [],
    tiers: [],
    churnReason: "",
  });

  const [period, setPeriod] = useState({
    from: moment().startOf("month").toDate(),
    to: new Date(),
  });

  /* Data */

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useSalesChurnReportsQuery({
    payload: {
      order: prepareReqOrder(order),
      period: prepareReqPeriod(period),
      filter: prepareReqFilter(filter),
      pageSize: 15,
    },
    options: {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: () => {
        uiNotification.error(tCommon("error.getReports"));
      },
    },
  });

  const reports = useMemo(() => {
    if (data?.pages) {
      return data.pages
        .map((p) => p.reports)
        .reduce((r, p) => [...r, ...p], []);
    }
    return [];
  }, [data?.pages]);

  const stats = unwrapOr(() => data.pages[0].stats, {});

  /* Statuses */

  const isSoftLoading =
    Object.values({
      ...filter,
      ...order,
      ...period,
    }).some(hasLength) &&
    isFetching &&
    !isLoading &&
    !isFetchingNextPage;

  const isFilterActive = Object.values(filter).some(hasLength);

  // ---------

  return {
    reports: {
      value: reports,
      hasMore: Boolean(hasNextPage),
      isNextFetching: isFetchingNextPage,
      isLoading: isLoading,
      fetchMore: fetchNextPage,
      isSoftLoading,
      total: stats.totalChurn || 0,
    },
    filter: {
      value: filter,
      isActive: isFilterActive,
      update: setFilter,
    },
    order: {
      value: order,
      update: updateOrder,
    },
    period: {
      value: period,
      update: setPeriod,
    },
    clinics: data?.pages[0]?.clinics || [],
  };
}
